import React, { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { makeStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";

// --- Import Download Libraries ---
import html2canvas from "html2canvas";
import PptxGenJS from "pptxgenjs";

import { useRccTypography } from "./typography";
import { rccColors } from "./colors";
import FocusPeriodCard from "./FocusPeriodCard";

const FILTER_LABELS = {
  closed_fiscal_quarter: "Closed Fiscal Qtr",
  fiscal_quarter: "Fiscal Qtr",
  lookback_quarter: "Lookback Period",
  // NOTE: offset_days label is dynamic (quarter vs month) based on other filters.
  offset_days: "Day in Qtr",
  INCLUDE_ALL_CQ_WON: "Include all CQ Won",
  exclude_acv_lte_zero: "Exclude large deals",
  Solution_Cat: "Solution Category",
  cross_sell1__c: "Sales Type",
  Type: "Opportunity Type",
  Acct_Size_Segment: "Customer Segment",
  Lead_Source_Category: "Lead Source Category",
  cycle_time_from_stage: "Cycle Time From Stage",
  stage: "Earliest Open Stage",
};

// Filters that should only display the first value (not all values)
const FIRST_VALUE_ONLY_FILTERS = ["cycle_time_from_stage", "stage"];

function toTitleCase(input) {
  if (!input) return "";
  return String(input)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ""))
    .join(" ");
}

function normalizeFilterValues(value) {
  if (value == null) return [];
  if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
  return [String(value)].filter(Boolean);
}

function isNonRestrictive(values) {
  if (!values || values.length === 0) return true;
  if (values.length === 1 && values[0].toLowerCase() === "all") return true;
  return false;
}

function hasMonthlyTimeContext(appliedFilters) {
  if (!Array.isArray(appliedFilters)) return false;
  return appliedFilters.some((f) => {
    const name = f?.name;
    return name === "fiscal_month" || name === "lookback_month";
  });
}

function getFilterLabel(name, appliedFilters) {
  if (name === "offset_days") {
    return hasMonthlyTimeContext(appliedFilters)
      ? "Day in Month"
      : "Day in Qtr";
  }
  return FILTER_LABELS[name] || toTitleCase(name);
}

function formatAppliedFiltersForChips(appliedFilters) {
  if (!Array.isArray(appliedFilters)) return [];

  return appliedFilters
    .map((f) => {
      const name = f?.name;
      if (!name) return null;

      const values = normalizeFilterValues(f?.value);
      if (isNonRestrictive(values)) return null;

      const label = getFilterLabel(name, appliedFilters);
      return { key: name, label, values };
    })
    .filter(Boolean);
}

function formatFiltersForSubtitle(appliedFilters, excludeFilters = []) {
  if (!Array.isArray(appliedFilters)) return [];

  const excludeSet = new Set(excludeFilters);

  const filteredItems = appliedFilters
    .map((f) => {
      const name = f?.name;
      if (!name || excludeSet.has(name)) return null;

      const values = normalizeFilterValues(f?.value);
      // Hide filters with empty or "All" values
      if (
        !values.length ||
        (values.length === 1 && values[0].toLowerCase() === "all")
      ) {
        return null;
      }

      const label = getFilterLabel(name, appliedFilters);

      // For certain filters, only show the first value
      const displayValues = FIRST_VALUE_ONLY_FILTERS.includes(name)
        ? values[0]
        : values.join(", ");

      return { label, values: displayValues };
    })
    .filter(Boolean);

  return filteredItems;
}

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: "12px",
    backgroundColor: "#FFFFFF",
    boxShadow: "6px 6px 54px 0 rgba(0, 0, 0, 0.05)",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
    },
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  contentRoot: {
    display: "flex",
    padding: "16px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "24px",
    flex: "1 0 0",
    alignSelf: "stretch",
    height: "100%",
    position: "relative", // Added for absolute positioning of filter overlay
    "&:last-child": {
      paddingBottom: "16px",
    },
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    alignSelf: "stretch",
    width: "100%",
    gap: "16px",
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
  },
  titleText: {
    lineHeight: 1.4,
  },
  actionsSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
    marginTop: "2px",
  },
  iconButton: {
    cursor: "pointer",
    color: rccColors.neutral[300],
    "&:hover": {
      color: rccColors.neutral[600],
    },
    userSelect: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative", // Needed for loading spinner positioning
  },
  materialIcon: {
    fontSize: "1.2rem",
    width: "20px",
    height: "20px",
    display: "block",
    lineHeight: 1,
  },
  childrenContainer: {
    width: "100%",
    flex: 1,
    minHeight: "300px",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#FFFFFF", // Important for html2canvas
  },
  filterPopover: {
    position: "absolute",
    top: "48px", // Position 16px below the title/button row
    left: "16px",
    right: "16px",
    padding: "8px",
    backgroundColor: rccColors.blue[50], // Blue/50
    borderRadius: "8px",
    zIndex: 10,
    boxShadow:
      "0 1px 3px 0 rgba(10, 13, 18, 0.10), 0 1px 2px 0 rgba(10, 13, 18, 0.06)",
  },
  filterChipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "8px",
    rowGap: "4px",
  },
  filterChip: {
    backgroundColor: rccColors.materialGrey[0], // materialGrey/0 (using 50 as lightest available)
    border: "none",
    borderRadius: "9999px", // Full rounding (pill shape)
    color: rccColors.neutral[700], // Gray/700
    height: "auto",
    padding: "4px 8px",
    "& .MuiChip-label": {
      padding: 0,
      whiteSpace: "normal",
    },
  },
  filterSubtitle: {
    marginTop: "-20px", // Reduce gap between title and subtitle
  },
  filterSubtitleText: {
    color: rccColors.neutral[600],
  },
}));

const RccCard = ({
  title,
  isLoading,
  children,
  onInfoClick,
  onZoomClick,
  onRedirectClick,
  className,
  redirectToDashboard,
  appliedFilters,
  focusVariant,
  focusLabel,
  excludeFilters,
}) => {
  const classes = useStyles();
  const typography = useRccTypography();
  const history = useHistory();

  // 1. Create a ref to capture the chart/table container
  const contentRef = useRef(null);

  // 2. State to handle download loading spinner
  const [isDownloading, setIsDownloading] = useState(false);

  // 3. State for filter chips visibility
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  const chipFilters = useMemo(() => {
    const allChips = formatAppliedFiltersForChips(appliedFilters);
    if (!excludeFilters || excludeFilters.length === 0) return allChips;
    const excludeSet = new Set(excludeFilters);
    return allChips.filter((chip) => !excludeSet.has(chip.key));
  }, [appliedFilters, excludeFilters]);
  const canShowFilters = chipFilters.length > 0;

  const subtitleFilters = useMemo(
    () => formatFiltersForSubtitle(appliedFilters, excludeFilters),
    [appliedFilters, excludeFilters],
  );

  const canShowRedirect =
    typeof redirectToDashboard === "string" &&
    redirectToDashboard.trim() !== "";

  // Get elements from Redux
  const elements = useSelector((state) => state.app.user.elements);

  // Calculate navItem dynamically
  const activeNavItem = useMemo(() => {
    if (!elements || !redirectToDashboard) return null;

    // Search through elements to find the group containing the redirectToDashboard (dashboard_id)
    const foundGroup = elements.find(
      (group) =>
        group.navItem !== "Favorites" && // Skip if group is Favorites
        group.charts &&
        group.charts.some((chart) => chart.Chart_Name === redirectToDashboard),
    );
    return foundGroup ? foundGroup.navItem : null;
  }, [elements, redirectToDashboard]);

  const handleGlobalRedirect = () => {
    if (!activeNavItem || !redirectToDashboard) return;

    const navMap = {
      "Enhanced Insights": "enhancedPage",
      "Rep Performance": "repPage",
      "Other Insights": "othersPage",
      "Marketing Support Requests": "marketPage",
      "Growth Modeling": "growthModelingPage",
      "Pipeline Build & Sufficiency": "pipelineBuildandSuficencyPage",
      "Pipeline Velocity & Conversion": "pipelineVelocityandConversionPage",
      "Market Intel & Growth Levers": "marketIntelandGrowthLeversPage",
      "Sales Productivity": "salesProductivityPage",
      "Growth Science Insights": "growthScienceInsightsPage",
      "Custom Scorecards": "personalizedScorecardsPage",
      Settings: "SettingsPage",
      "Product Performance": "productPage",
    };

    const currentKey = navMap[activeNavItem];
    if (currentKey) {
      localStorage.setItem("skygeni-user-page", activeNavItem);
      localStorage.setItem(`skygeni-user-${currentKey}`, redirectToDashboard);

      // Set RCC source marker for "Back to RCC" button (store target page and dashboard)
      sessionStorage.setItem(
        "skygeni-rcc-source",
        JSON.stringify({
          group: activeNavItem,
          dashboard: redirectToDashboard,
        }),
      );

      // Remove all other pages
      Object.values(navMap).forEach((key) => {
        if (key !== currentKey) {
          localStorage.removeItem(`skygeni-user-${key}`);
        }
      });
    }
    history.push("/");
    window.location.reload();
  };

  // --- 3. DOWNLOAD LOGIC ---
  const handleDownload = async () => {
    if (!contentRef.current) return;

    setIsDownloading(true);

    try {
      // A. Capture the contentRef as an image
      const canvas = await html2canvas(contentRef.current, {
        backgroundColor: "#FFFFFF", // Ensure white background
        scale: 2, // Higher resolution
        useCORS: true, // Helpful if you have external images
      });

      const imageBase64 = canvas.toDataURL("image/png");

      // B. Generate PPT
      const pptx = new PptxGenJS();
      const slide = pptx.addSlide({ background: { fill: "FFFFFF" } });

      // Layout calculations (Logic from your snippet)
      const slideWidthInches = 10; // Standard 16:9 is usually 10x5.625 or 13.3x7.5
      const slideHeightInches = 5.625;

      const imgWidthPx = canvas.width;
      const imgHeightPx = canvas.height;
      const imageAspectRatio = imgWidthPx / imgHeightPx;
      const slideAspectRatio = slideWidthInches / slideHeightInches;

      let widthInches, heightInches;

      // Fit image within slide bounds
      if (imageAspectRatio > slideAspectRatio) {
        widthInches = slideWidthInches - 1; // Leave 0.5 inch margin
        heightInches = widthInches / imageAspectRatio;
      } else {
        heightInches = slideHeightInches - 1.5; // Leave space for title
        widthInches = heightInches * imageAspectRatio;
      }

      const xPos = (slideWidthInches - widthInches) / 2;
      const yPos = (slideHeightInches - heightInches) / 2 + 0.5; // Offset for title

      // Add Title
      slide.addText(title, {
        x: 0.5,
        y: 0.2,
        w: slideWidthInches - 1,
        h: 0.5,
        fontSize: 18,
        align: "center",
        color: "363636",
        bold: true,
      });

      // Add Image
      slide.addImage({
        data: imageBase64,
        x: xPos,
        y: yPos,
        w: widthInches,
        h: heightInches,
      });

      // Save File
      await pptx.writeFile(
        `SkyGeni-${title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.pptx`,
      );
    } catch (error) {
      console.error("PPT Generation Error:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  // --- Render Loading State ---
  if (isLoading) {
    return (
      <Card className={`${classes.card} ${className || ""}`}>
        <CardContent className={classes.contentRoot}>
          <Box className={classes.headerContainer} mb={2}>
            <Skeleton variant="text" width={180} height={24} />
            <Skeleton variant="circle" width={20} height={20} />
          </Box>
          <Skeleton
            variant="rect"
            width="100%"
            height="100%"
            animation="wave"
            style={{ borderRadius: "8px", flex: 1 }}
          />
        </CardContent>
      </Card>
    );
  }

  // --- Render Loaded State ---
  return (
    <Card className={`${classes.card} ${className || ""}`}>
      <CardContent className={classes.contentRoot}>
        {/* Header */}
        <Box className={classes.headerContainer}>
          {/* Title Section */}
          <div className={classes.titleSection}>
            <Typography
              className={`${typography.textMediumSemibold} ${classes.titleText}`}
              style={{ color: rccColors.neutral[800] }}
            >
              {title}
            </Typography>

            {/* Render the Focus Card */}
            {focusVariant && (
              <FocusPeriodCard variant={focusVariant} label={focusLabel} />
            )}

            {/* Filter Popover Arrow Icon */}
            {/* {canShowFilters && (
              <div
                className={classes.iconButton}
                onClick={() => setFilterPopoverOpen(!filterPopoverOpen)}
                title="Show Filters"
                style={{
                  backgroundColor: filterPopoverOpen
                    ? rccColors.blue[50]
                    : "transparent",
                  borderRadius: "50%",
                  width: "24px",
                  height: "24px",
                  transition: "background-color 0.2s ease",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span
                  className={`material-icons`}
                  style={{
                    fontSize: "16px",
                    lineHeight: 1,
                    display: "block",
                  }}
                >
                  {filterPopoverOpen
                    ? "keyboard_arrow_up"
                    : "keyboard_arrow_down"}
                </span>
              </div>
            )} */}

            {/* Hidden Info Icon (as per previous request) */}
            {/* <div className={classes.iconButton} onClick={onInfoClick} title="Information" style={{ marginTop: "2px" }}>
              <span className={`material-icons ${classes.materialIcon}`}>info_outlined</span>
            </div> */}
          </div>

          {/* Actions Section */}
          <div className={classes.actionsSection}>
            {/* DOWNLOAD ICON */}
            <div
              className={classes.iconButton}
              onClick={!isDownloading ? handleDownload : undefined}
              title="Download PPT"
              style={{ cursor: isDownloading ? "wait" : "pointer" }}
            >
              {isDownloading ? (
                // Show Spinner while downloading
                <CircularProgress
                  size={18}
                  thickness={5}
                  style={{ color: rccColors.neutral[400] }}
                />
              ) : (
                <span className={`material-icons ${classes.materialIcon}`}>
                  download
                </span>
              )}
            </div>

            {/* Zoom Icon */}
            {/* {onZoomClick && (
              <div
                className={classes.iconButton}
                onClick={onZoomClick}
                title="Zoom"
              >
                <span className={`material-icons ${classes.materialIcon}`}>
                  zoom_in
                </span>
              </div>
            )} */}

            {/* Redirect Icon */}
            {canShowRedirect && (onRedirectClick || activeNavItem) && (
              <div
                className={classes.iconButton}
                onClick={onRedirectClick || handleGlobalRedirect}
                title="Go to Dashboard"
              >
                <span className={`material-icons ${classes.materialIcon}`}>
                  arrow_outward
                </span>
              </div>
            )}
          </div>
        </Box>

        {/* Filter Subtitle - shown below title */}
        {subtitleFilters.length > 0 && (
          <Box className={classes.filterSubtitle}>
            <Typography
              component="span"
              className={`${typography.textSmallRegular} ${classes.filterSubtitleText}`}
            >
              (
              {subtitleFilters.map((filter, index) => (
                <React.Fragment key={filter.label}>
                  {index > 0 && " | "}
                  {filter.label}:{" "}
                  <span style={{ fontWeight: 600 }}>{filter.values}</span>
                </React.Fragment>
              ))}
              )
            </Typography>
          </Box>
        )}

        {/* Filter Chips - Displayed when popover is open */}
        {filterPopoverOpen && canShowFilters && (
          <Box className={classes.filterPopover}>
            <Box className={classes.filterChipsContainer}>
              {chipFilters.map((filter) => (
                <Chip
                  key={filter.key}
                  label={
                    <span className={typography.textLabelRegular}>
                      {filter.label}:{" "}
                      <span style={{ fontWeight: 600 }}>
                        {filter.values.join(", ")}
                      </span>
                    </span>
                  }
                  className={classes.filterChip}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Card Content - Attached REF here */}
        <Box
          className={classes.childrenContainer}
          ref={contentRef} // <--- THIS IS THE KEY
        >
          {children}
        </Box>
      </CardContent>
    </Card>
  );
};

RccCard.propTypes = {
  title: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  children: PropTypes.node,
  onInfoClick: PropTypes.func,
  onZoomClick: PropTypes.func,
  onRedirectClick: PropTypes.func,
  className: PropTypes.string,
  redirectToDashboard: PropTypes.string,
  appliedFilters: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.any,
    }),
  ),
  focusVariant: PropTypes.oneOf(["current", "future"]),
  focusLabel: PropTypes.string,
  excludeFilters: PropTypes.arrayOf(PropTypes.string),
};

RccCard.defaultProps = {
  isLoading: false,
  onInfoClick: () => {},
  onDownloadClick: () => {},
  onZoomClick: null, // Hidden by default
  onRedirectClick: null, // Hidden by default
  redirectToDashboard: null,
  appliedFilters: [],
  focusVariant: null,
  focusLabel: null,
  excludeFilters: [
    "closed_fiscal_quarter",
    "projected_close_fiscal_quarter",
    "Closed_Year_Month",
    "Projected_Close_Year_Month",
    "exclude_acv_lte_zero",
  ],
};

export default RccCard;
